import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Loader } from 'semantic-ui-react';
import styled from 'styled-components';
import moment from 'moment-timezone';
import ClaimInfoHeader from '../../ClaimInfoHeader';
import {
  ClaimSynthesisResponse,
  generateClaimSynthesis
} from '../../../../../../services/claim';
import {
  dangerNotification,
  warningNotification
} from '../../../../../../services/notification';

const CLAIM_UPDATES_ACCENT_COLOR = '#59249B';
const SYNTHESIS_IN_PROGRESS = 'CLAIM_SYNTHESIS_IN_PROGRESS';

const LoadingState = styled.div`
  min-height: 260px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  color: #59249b;
`;

const LoadingMessage = styled.div`
  color: #59249b;
  font-size: 15px;
  font-weight: 600;
`;

const PendencyBadge = styled.div`
  display: inline-block;
  margin: 0 0 12px;
  padding: 6px 10px;
  border-radius: 6px;
  background: #fff4e5;
  color: #8a4b00;
  font-size: 13px;
`;

const SectionTitle = styled.div`
  color: #2f3441;
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 10px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;

  @media (max-width: 820px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const SynthesisCard = styled.section`
  background: #f7f3fb;
  border-radius: 8px;
  padding: 18px 16px;
  color: #59249b;
  min-width: 0;
`;

const CardTitle = styled.h3`
  margin: 0 0 8px;
  color: #59249b;
  font-size: 17px;
`;

const CardMeta = styled.div`
  margin-top: 18px;
  color: #707682;
  font-size: 12px;
  text-align: right;
`;

const Summary = styled.p`
  margin: 0 0 14px;
  color: #59249b;
  line-height: 1.45;
`;

const Label = styled.strong`
  color: #3f256a;
`;

const List = styled.ul`
  margin: 6px 0 0;
  padding-left: 20px;
  line-height: 1.45;
`;

const HistoryList = styled.ol`
  margin: 0;
  padding-left: 22px;
  color: #59249b;
  line-height: 1.45;

  li {
    padding: 0 0 10px 4px;
  }
`;

const EmptyState = styled.p`
  margin: 0;
  color: #707682;
`;

type SynthesisTabProps = {
  id: string;
  claim: any;
  onLoadingChange?: (isLoading: boolean) => void;
};

const WAITING_LABELS: Record<string, string> = {
  SEGURADORA: 'Seguradora',
  CORRETORA: 'Corretora',
  IMOBILIARIA: 'Imobiliária',
  NENHUM: 'Ninguém',
  INDEFINIDO: 'Não identificado'
};

const SOURCE_LABELS: Record<string, string> = {
  CORRETORA: 'Corretora',
  IMOBILIARIA: 'Imobiliária',
  SEGURADORA: 'Seguradora',
  SISTEMA: 'Sistema'
};

const SynthesisTab = ({ id, claim, onLoadingChange }: SynthesisTabProps) => {
  const [synthesis, setSynthesis] = useState<ClaimSynthesisResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const loadSynthesis = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    onLoadingChange?.(true);
    try {
      const response = await generateClaimSynthesis(id);
      if (isMounted.current) {
        setSynthesis(response);
      }
    } catch (error) {
      if (!isMounted.current) {
        return;
      }
      const apiError = error as {
        message?: string;
        errorCode?: string;
        data?: { message?: string; errorCode?: string };
      };
      const errorCode = apiError?.errorCode || apiError?.data?.errorCode;
      const message =
        apiError?.message ||
        apiError?.data?.message ||
        'Não foi possível gerar a síntese. Tente novamente em instantes.';
      setErrorMessage(message);
      if (errorCode === SYNTHESIS_IN_PROGRESS) {
        warningNotification('Síntese em andamento', message);
      } else {
        dangerNotification('Não foi possível gerar a síntese', message);
      }
    } finally {
      onLoadingChange?.(false);
      if (isMounted.current) {
        setIsLoading(false);
      }
    }
  }, [id, onLoadingChange]);

  useEffect(() => {
    loadSynthesis();
  }, [loadSynthesis]);

  if (isLoading) {
    return (
      <>
        <ClaimInfoHeader claim={claim} />
        <LoadingState>
          <Loader active inline="centered" size="large" />
          <LoadingMessage>Gerando síntese do sinistro...</LoadingMessage>
        </LoadingState>
      </>
    );
  }

  if (errorMessage || !synthesis) {
    return (
      <>
        <ClaimInfoHeader claim={claim} />
        <EmptyState>{errorMessage || 'Síntese indisponível.'}</EmptyState>
        <Button
          type="button"
          onClick={loadSynthesis}
          style={{ marginTop: 12, background: CLAIM_UPDATES_ACCENT_COLOR, color: '#fff' }}
        >
          Tentar novamente
        </Button>
      </>
    );
  }

  const updatedAt = synthesis.sourceUpdatedAt || synthesis.generatedAt;
  const formattedUpdatedAt = updatedAt
    ? moment(updatedAt).format('DD/MM/YYYY [às] HH:mm')
    : 'data não informada';
  const situation = synthesis.currentSituation;
  const pendency = synthesis.currentPendency;

  return (
    <>
      <ClaimInfoHeader claim={claim} />
      <SectionTitle>Síntese do Sinistro</SectionTitle>
      {pendency?.responsible && (
        <PendencyBadge>
          <Label>Pendência atual: </Label>
          {pendency.responsible}
          {pendency.type ? ` · ${pendency.type}` : ''}
        </PendencyBadge>
      )}
      <CardsGrid>
        <SynthesisCard>
          <CardTitle>Situação atual</CardTitle>
          <Summary>{situation.summary}</Summary>
          <div>
            <Label>Aguardando: </Label>
            {WAITING_LABELS[situation.waitingOn] || situation.waitingOn}
          </div>
          {situation.pendingItems.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Label>Pendências</Label>
              <List>
                {situation.pendingItems.map((item, index) => (
                  <li key={`${item}-${index}`}>{item}</li>
                ))}
              </List>
            </div>
          )}
          {situation.keyDates.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <Label>Datas importantes</Label>
              <List>
                {situation.keyDates.map((item, index) => (
                  <li key={`${item.description}-${index}`}>
                    {item.description}: {item.date}
                  </li>
                ))}
              </List>
            </div>
          )}
          <CardMeta>Atualizado em {formattedUpdatedAt}</CardMeta>
        </SynthesisCard>

        <SynthesisCard>
          <CardTitle>Histórico do sinistro</CardTitle>
          {synthesis.history.steps.length > 0 ? (
            <HistoryList>
              {synthesis.history.steps.map((step, index) => (
                <li key={`${step.description}-${index}`}>
                  <Label>
                    {step.date ? `${step.date} — ` : ''}
                    {SOURCE_LABELS[step.source] || step.source}
                  </Label>
                  <div>{step.description}</div>
                </li>
              ))}
            </HistoryList>
          ) : (
            <EmptyState>Não há histórico suficiente para exibir.</EmptyState>
          )}
          <CardMeta>Atualizado em {formattedUpdatedAt}</CardMeta>
        </SynthesisCard>
      </CardsGrid>
    </>
  );
};

export default SynthesisTab;
